import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { StyledCheckbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFormContext } from '@/components/react-hook-form';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { isEligibleForInflation } from '@/modules/dispositiveProvisions/dispositiveProvisions.utils';
import {
  GROW_BY_INFLATION_HELP_BODY,
  GrowByInflationPopperContent,
} from '@/modules/entities/EntityBeneficiariesSubform/GrowByInflationCheckbox';
import {
  GrowthProfilesTrowser,
  GrowthProfilesTrowserScope,
  growthProfilesTrowserTarget,
} from '@/modules/growthProfiles/GrowthProfilesTrowser/GrowthProfilesTrowser';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { COLORS } from '@/styles/tokens/colors';
import { DispositiveProvisionDispositionKind } from '@/types/schema';

interface InflationEligibleRecipient {
  growByInflation?: boolean;
  dispositionKind_ButtonGroup:
    | DispositiveProvisionDispositionKind.Percentage
    | DispositiveProvisionDispositionKind.Amount
    | 'other'
    | 'asset';
}

/** Noun shown in the menu label + tooltip; keys into GROW_BY_INFLATION_HELP_BODY. */
type GrowByInflationSubject = keyof typeof GROW_BY_INFLATION_HELP_BODY;

const MENU_LABEL_BY_SUBJECT: Record<GrowByInflationSubject, string> = {
  disposition: 'Grow all dispositions by inflation',
  distribution: 'Grow all distributions by inflation',
};

interface ToggleGrowAllByInflationMenuItemProps {
  recipients: InflationEligibleRecipient[];
  /** Field path prefix before `.recipients.${index}.growByInflation` */
  recipientsBasePath: string;
  /** Noun for the menu label + tooltip copy. Defaults to dispositive-provisions copy. */
  subject?: GrowByInflationSubject;
}

export function ToggleGrowAllByInflationMenuItem({
  recipients,
  recipientsBasePath,
  subject = 'disposition',
}: ToggleGrowAllByInflationMenuItemProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { setValue, control } = useFormContext();
  const growthProfilesTrowser = useTrowser(TrowserKey.GrowthProfiles);
  // One menu renders per dispositions form, so its recipients path identifies
  // this menu's trowser and a click opens only this menu's copy.
  const trowserTarget = growthProfilesTrowserTarget(
    GrowthProfilesTrowserScope.GrowAllByInflationMenu,
    recipientsBasePath
  );

  // useWatch subscribes to field-level changes (e.g. from setValue), unlike
  // useFieldArray.fields which only reflects structural changes (append/remove).
  // This ensures the checkbox state updates after toggling individual fields.
  const watchedRecipients = useWatch({
    control,
    name: `${recipientsBasePath}.recipients`,
  }) as InflationEligibleRecipient[] | undefined;

  const currentRecipients = watchedRecipients ?? recipients;

  const eligibleRecipients = useMemo(
    () =>
      currentRecipients
        .map((r, i) => ({ ...r, index: i }))
        .filter((r) => isEligibleForInflation(r)),
    [currentRecipients]
  );

  // Check all recipients for the "all checked" state (including formulaic,
  // which are always forced on) so the checkbox accurately reflects reality.
  const allGrowByInflation = useMemo(
    () =>
      currentRecipients.length > 0 &&
      currentRecipients.every((r) => r.growByInflation === true),
    [currentRecipients]
  );

  // Only toggle eligible (non-formulaic) recipients since formulaic
  // dispositions always grow by inflation and cannot be toggled off.
  const handleToggle = useCallback(() => {
    const newValue = !allGrowByInflation;
    eligibleRecipients.forEach(({ index }) => {
      setValue(
        `${recipientsBasePath}.recipients.${index}.growByInflation`,
        newValue,
        { shouldDirty: true }
      );
    });
  }, [allGrowByInflation, eligibleRecipients, recipientsBasePath, setValue]);

  const hasEligibleRecipients = eligibleRecipients.length > 0;

  if (!currentRecipients.length) {
    return null;
  }

  return (
    <>
      <MenuItem
        key="grow-all-by-inflation-toggle"
        label={
          <Stack direction="row" alignItems="center" spacing={1}>
            <StyledCheckbox
              onChange={handleToggle}
              value={allGrowByInflation}
              disabled={!hasEligibleRecipients}
            />
            <Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>
              {MENU_LABEL_BY_SUBJECT[subject]}
            </Typography>
            <ContextualHelpTooltip>
              <GrowByInflationPopperContent
                trowserTarget={trowserTarget}
                body={GROW_BY_INFLATION_HELP_BODY[subject]}
              />
            </ContextualHelpTooltip>
          </Stack>
        }
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
      {growthProfilesTrowser.target === trowserTarget && householdId && (
        <GrowthProfilesTrowser
          onClose={growthProfilesTrowser.close}
          householdId={householdId}
        />
      )}
    </>
  );
}
